import { Form } from "antd"
import { Flex, Group, Icon, Legend, notification, Number, RadioButtons, Tag, Text, TextInput } from "components/common"
import { useCallback, useEffect, useMemo } from "react"
import { customValidatorForAddPayment, customValidatorForVASAddPayment, fieldMapper } from "../../../../utils"
import {
  cardPaymentFormChildren,
  cardPaymentFormProps,
  chequeFormChildren,
  chequeFormProps,
  depositSlipFormChildren,
  depositSlipFormProps,
  onlinePaymentFormChildren,
  onlinePaymentFormProps,
  payorderFormChildren,
  payorderFormProps,
} from "../../../../utils/formItemConstants"
import { t } from "i18next"
import PaymentProofFormItems from "./paymentProofFormItems"
import { useSelector } from "react-redux"
import { numberProps } from "features/leads/components/addSalesModal/utils/constants"
import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"
import _ from "lodash"
import IBANInfo from "features/shared/components/ibanInfo/ibanInfo"
import getPrivilege from "utils/privileges/privileges"

const PaymentSection = ({ rules, clientIbanRef }) => {
  const {
    addSalesApis,
    form,
    stateManager,
    formWatchers,
    minTokenDetails,
    isEscrowSelfFinance,
    lastPaymentStatus,
    lastPaymentType,
    client,
    lead,
    showNextPayment,
    vasEnabled,
    vasFees,
  } = useAddSalesContext()
  const { project, unit, cdpCommitment, amount } = formWatchers
  const {
    leadSalesInfo,
    taskTypes,
    saleEvent,
    referenceError,
    modeOfPayments,
    banks,
    getBankName,
    validateReference,
    getPaymentMode,
    referenceValidity,
    paymentMode,
    getContractExpiry,
    viban,
    fetchingVIBAN,
    clientIban,
  } = addSalesApis

  const { getFieldValue, setFieldsValue, validateFields } = form
  const depositSlip = Form.useWatch("bank_deposit_slip", form)
  const onlinePayment = Form.useWatch("online_payment_screenshot", form)
  const cheque = Form.useWatch("cheque", form)
  const cardPayment = Form.useWatch("receipt", form)
  const modeOfPayment = Form.useWatch("modeOfPayment", form)

  const userPlateformKey = useSelector(state => state.app.user?.info?.AgencyConfiguration?.Platform?.platformKey)

  const debouncedReferenceValidator = useCallback(
    _.debounce(value => {
      validateReference(value)
        .unwrap()
        .then(e => {
          validateFields(["referenceNumber"])
        })
        .catch(e => notification?.error({ message: e?.error }))
    }, 500),
    []
  )

  const debouncedGetContractExpiry = useCallback(
    _.debounce(params => {
      getContractExpiry(params)
        .unwrap()
        .catch(error => console.log(error))
    }, 500),
    []
  )

  useEffect(() => {
    if (cheque) {
      const chequeField = modeOfPayments?.find(item => item.key === "cheque")?.cheque
      fieldMapper(cheque?.imageData, chequeField, form)
      if (cheque?.imageData?.chequeNumber) {
        validateReference({
          referenceNumber: cheque?.imageData?.chequeNumber,
          bankKey: getFieldValue("chequeBank"),
          paymentModeKey: getFieldValue("modeOfPayment"),
        })
          .unwrap()
          .then(e => {
            validateFields(["referenceNumber"])
          })
      }
    }
    if (onlinePayment) {
      const onlinePaymentField = modeOfPayments?.find(item => item.key === "online_payment")?.onlinePayment
      fieldMapper(onlinePayment?.imageData, onlinePaymentField, form)
    }
  }, [cheque, onlinePayment, modeOfPayments])
  useEffect(() => {
    if (depositSlip) {
      const depositSlipField = modeOfPayments?.find(item =>
        ["cheque", "cash", "bank_order"].includes(item.key)
      )?.depositSlip
      fieldMapper(depositSlip?.imageData, depositSlipField, form)
    }
  }, [depositSlip])
  useEffect(() => {
    if (cardPayment) {
      const cardPaymentField = modeOfPayments?.find(item => item.key === "card_payment")?.cardPayment
      fieldMapper(cardPayment?.imageData, cardPaymentField, form)
    }
  }, [cardPayment])

  const onChangeAmount = useCallback(
    (amount, tokenDetails) => {
      form.setFieldValue("deadLine", null)
      updateNextTask(amount, tokenDetails)
      form
        .validateFields(["amount"])
        .then(() => {
          debouncedGetContractExpiry({
            listingId: unit?.listingId ?? leadSalesInfo?.listingId,
            params: {
              tokenAmount: amount,
              dealPrice: stateManager?.dealPrice,
              contractListingId: leadSalesInfo?.contracts?.[0]?.contractListingId,
            },
          })
        })
        .catch(({ errorFields }) =>
          errorFields?.forEach(item => item?.errors?.forEach(message => console.log(message)))
        )
    },
    [stateManager, leadSalesInfo]
  )

  const handleSuggestedAmountClick = element => {
    const numericValue = element.props.value
    form.setFieldsValue({ amount: numericValue })
    onChangeAmount(numericValue, minTokenDetails)
  }

  const calculateValue = useCallback(
    (value, unit, fallback) =>
      value !== undefined ? (unit === "percentage" ? (value / 100) * stateManager?.dealPrice : value) : fallback,
    [stateManager?.dealPrice]
  )

  const suffixHandler = useCallback(() => {
    const remainingCdp = leadSalesInfo?.remainComDowPayment
    const remainingPdp = leadSalesInfo?.remainParDowPayment
    const projectPdp = stateManager?.dealPrice * project?.pdpPercentage
    const projectCdp = stateManager?.dealPrice * (cdpCommitment / 100)
    const amountEntered = +amount
    let message = ""
    if (amountEntered !== null && stateManager?.dealPrice === 0) return message
    if (vasEnabled && leadSalesInfo?.isFirstSalesTask) {
      message = amountEntered === Math.round(vasFees) ? t("VAS Fee Done") : ""
    } else if (vasEnabled && leadSalesInfo?.isVasRemaining) {
      message = amountEntered === Math.round(leadSalesInfo?.vasRemianingAmount) ? t("VAS Remaining Fee Done") : ""
    } else if (leadSalesInfo?.current >= 0) {
      message =
        amountEntered >= remainingCdp
          ? t("Complete Payment Done")
          : amountEntered >= remainingPdp && amountEntered < remainingCdp
          ? t("Partial Payment Done")
          : ""
    } else {
      message =
        amountEntered >= projectCdp
          ? t("Complete Payment Done")
          : amountEntered >= projectPdp && amountEntered < projectCdp
          ? t("Partial Payment Done")
          : ""
    }
    return (
      <>
        {`${message ? `${message}:` : ``}`} <Number {...numberProps} value={amount} />
      </>
    )
  }, [amount, project, stateManager?.dealPrice, leadSalesInfo, t])

  const optionList = useMemo(() => {
    return {
      bank: banks,
      paymentMode: paymentMode,
    }
  }, [banks, paymentMode])

  const suggestedAmounts = useMemo(() => {
    const token = calculateValue(
      leadSalesInfo?.token,
      leadSalesInfo?.tokenUnit,
      calculateValue(minTokenDetails.minToken, minTokenDetails.minTokenUnit, 0)
    )

    const pdp = leadSalesInfo?.remainParDowPayment ?? project?.pdpPercentage * stateManager?.dealPrice

    const cdp =
      leadSalesInfo?.remainComDowPayment ??
      ((cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment) / 100) * stateManager?.dealPrice

    const components = []
    const addComponent = (value, suffix, key) => {
      components.push(<Number value={Math.round(value)} suffix={suffix} key={key} />)
    }

    const firstSales = leadSalesInfo?.isFirstSalesTask
    const vasRemaining = leadSalesInfo?.isVasRemaining
    const rejectedToken = lastPaymentStatus === "rejected" && lastPaymentType === "token"
    const noLead = !leadSalesInfo

    switch (true) {
      case vasEnabled && firstSales:
        addComponent(vasFees, "(VAS Fee)", "vas")
        break

      case vasEnabled && vasRemaining:
        addComponent(leadSalesInfo?.vasRemianingAmount, "(VAS Remaining Fee)", "vasRemain")
        break

      case noLead || firstSales || rejectedToken:
        addComponent(token, "(Token)", "token")
        if (!isEscrowSelfFinance) {
          addComponent(pdp, "(PDP)", "pdp")
          addComponent(cdp, "(CDP)", "cdp")
        }
        break

      default:
        if (pdp > 0) addComponent(pdp, "(PDP)", "pdp")
        if (cdp > 0) addComponent(cdp, "(CDP)", "cdp")
        break
    }

    return components
  }, [
    project,
    leadSalesInfo,
    stateManager?.dealPrice,
    cdpCommitment,
    minTokenDetails,
    isEscrowSelfFinance,
    vasEnabled,
    lastPaymentStatus,
    lastPaymentType,
    vasFees,
  ])

  const shouldRenderFormItem = type => {
    const hasPaymentProof =
      stateManager?.project?.paymentProof || project?.paymentProof || leadSalesInfo?.project?.paymentProof
    return !!((hasPaymentProof && type?.imageData) || (!hasPaymentProof && typeof type === "object"))
  }

  const updateNextTask = useCallback(
    (rawValue, tokenDetails) => {
      const value = +rawValue
      const remainingcdp = leadSalesInfo?.remainComDowPayment
      const remainingpdp = leadSalesInfo?.remainParDowPayment
      const baseAmount =
        saleEvent?.minAmount ??
        (tokenDetails?.tokenWithUnit > 1
          ? tokenDetails?.minToken
          : tokenDetails?.tokenWithUnit * stateManager?.dealPrice)
      let key
      switch (true) {
        case leadSalesInfo?.current >= 0 && value >= remainingcdp:
          key = "do_nothing"
          break
        case leadSalesInfo?.current >= 0 && value >= remainingpdp && value < remainingcdp:
          key = "complete_down_payment"
          break
        case leadSalesInfo?.current >= 0 && value < remainingpdp && value >= leadSalesInfo?.remainMinToken:
          key = "partial_down_payment"
          break
        case value >=
          Math.round(
            stateManager?.dealPrice * (cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment) * 0.01
          ):
          key = "do_nothing"
          break
        case value >= Math.round(stateManager?.dealPrice * project?.pdpPercentage) &&
          value <
            Math.round(
              stateManager?.dealPrice * (cdpCommitment ?? project?.ProjectFinancial?.completeDownPayment) * 0.01
            ):
          key = "complete_down_payment"
          break
        case value < Math.round(stateManager?.dealPrice * project?.pdpPercentage) &&
          value >= Math.round(vasEnabled ? vasFees : baseAmount):
          key = "partial_down_payment"
          break
        default:
          key = null
      }
      form.setFieldValue("nextTask", key ? taskTypes?.nextStatuses?.find(item => item.key === key)?.id : null)
    },
    [stateManager?.dealPrice, leadSalesInfo, project, taskTypes, cdpCommitment, vasEnabled, vasFees]
  )

  const formItemCreaterObject = {
    1: {
      conditionToCheckRender: shouldRenderFormItem(cardPayment, project, stateManager, leadSalesInfo),
      extraCondition: modeOfPayment === "card_payment",
      formItemPropList: cardPaymentFormProps(),
      formItemChildrenList: cardPaymentFormChildren({ setFieldsValue }),
    },
    5: {
      extraCondition: true,
      conditionToCheckRender: true,
      formItemPropList: payorderFormProps({
        referenceError,
        referenceValidity,
      }),
      formItemChildrenList: payorderFormChildren({
        setFieldsValue,
        optionList,
        getFieldValue,
        debouncedReferenceValidator,
      }),
    },
    2: {
      extraCondition: true,
      conditionToCheckRender: shouldRenderFormItem(depositSlip, project, stateManager, leadSalesInfo),
      formItemPropList: depositSlipFormProps(),
      formItemChildrenList: depositSlipFormChildren({
        setFieldsValue,
        optionList,
      }),
    },
    6: {
      extraCondition: true,
      conditionToCheckRender: shouldRenderFormItem(onlinePayment, project, stateManager, leadSalesInfo),
      formItemPropList: onlinePaymentFormProps(userPlateformKey),
      formItemChildrenList: onlinePaymentFormChildren({
        setFieldsValue,
        optionList,
      }),
    },
    3: {
      extraCondition: true,
      conditionToCheckRender: shouldRenderFormItem(cheque, project, stateManager, leadSalesInfo),
      formItemPropList: chequeFormProps({
        referenceError,
        referenceValidity,
      }),
      formItemChildrenList: chequeFormChildren({
        setFieldsValue,
        optionList,
        getFieldValue,
        debouncedReferenceValidator,
      }),
    },
  }

  return (
    <>
      {!!!leadSalesInfo?.verifiedWon && (leadSalesInfo?.unitPrice || (unit && project)) && (
        <>
          {isEscrowSelfFinance && !leadSalesInfo?.isFirstSalesTask && lastPaymentStatus !== "rejected" && (
            <IBANInfo
              clientId={client?.id}
              clientIbanRef={clientIbanRef}
              vIBAN={viban?.data?.virtualIban}
              contractId={leadSalesInfo?.contracts?.[0]?.contractId}
              clientIBAN={clientIban?.data?.iban}
              disableButton={lastPaymentStatus !== "completed"}
              loading={fetchingVIBAN}
              whatsappButtonDisabled={!getPrivilege("can_view_client_contact", lead?.privileges)}
            />
          )}
          {showNextPayment && (
            <div>
              <Legend title={t("Add Payment")} />
              <Group template="repeat(1, 1fr)" gap="16px 32px">
                <Form.Item
                  name="amount"
                  label={t("Amount")}
                  dependencies={["saleType"]}
                  required
                  onChange={event => {
                    onChangeAmount(event.target.value, minTokenDetails)
                  }}
                  extra={
                    !!suggestedAmounts?.length && (
                      <Flex gap="8px" className="pb-8">
                        <Text type="secondaryLight">{t("Suggestions")}:</Text>
                        <div>
                          {suggestedAmounts?.map(element => (
                            <Tag
                              type="light"
                              color="#f2faf4"
                              onClick={() => handleSuggestedAmountClick(element)}
                              tooltipText={element.props.value}
                              style={{
                                border: "1px solid #9d9d9d",
                              }}
                              className="pb-4 pi-12 pointer"
                              pill
                            >
                              <Icon icon="HiPlus" size={12} style={{ marginInlineEnd: 4, color: "#9d9d9d" }} />
                              <Text type="secondaryLight">{element}</Text>
                            </Tag>
                          ))}
                        </div>
                      </Flex>
                    )
                  }
                  rules={rules.concat({
                    validator: async (_, value) => {
                      if (vasEnabled && leadSalesInfo?.isFirstSalesTask) {
                        return customValidatorForVASAddPayment(value, leadSalesInfo, stateManager, vasEnabled, vasFees)
                      } else if (vasEnabled && leadSalesInfo?.isVasRemaining) {
                        return customValidatorForVASAddPayment(
                          value,
                          leadSalesInfo,
                          stateManager,
                          vasEnabled,
                          leadSalesInfo?.vasRemianingAmount
                        )
                      }
                      return customValidatorForAddPayment(
                        value,
                        saleEvent,
                        minTokenDetails,
                        leadSalesInfo,
                        stateManager,
                        isEscrowSelfFinance
                      )
                    },
                  })}
                  initialValue={null}
                >
                  <TextInput
                    placeholder={t("Enter amount")}
                    suffixText={suffixHandler()}
                    disabled={!leadSalesInfo?.listingId && (!project || !unit)}
                  />
                </Form.Item>
                <Group className="span-all" template="initial" gap="8px">
                  {(!!project || !!leadSalesInfo?.projectName) && (
                    <Form.Item
                      className="mbe-0"
                      required
                      name={"modeOfPayment"}
                      label={t("Mode Of Payment")}
                      rules={rules}
                    >
                      <RadioButtons
                        getValue={item => item?.key}
                        options={modeOfPayments}
                        onChange={value => {
                          const option = modeOfPayments?.find(e => e.key === value.target.value)
                          getPaymentMode(option.id, true)
                          if (option?.getBankNames) {
                            getBankName()
                          }
                        }}
                      />
                    </Form.Item>
                  )}
                  <PaymentProofFormItems rules={rules} />
                </Group>
              </Group>
            </div>
          )}
        </>
      )}
    </>
  )
}
export default PaymentSection
